// src/components/LoginContent.jsx

import React from 'react';
import {
  FaPlus,
  FaUser,
  FaLock,
  FaSignInAlt,
  FaCalendarAlt,
  FaGift,
  FaBriefcase,
  FaExclamationCircle,
  FaQrcode,
  FaCheckCircle,
  FaHeart,
} from 'react-icons/fa';
import PinLogin from './PinLogin';
import KeychainSetup from './KeychainSetup';
import MfaLogin from './MfaLogin';

const LoginContent = ({
  t,
  newAccountUrl,
  username,
  setUsername,
  password,
  handlePasswordChange,
  rememberMe,
  setRememberMe,
  loading,
  error,
  success,
  showPinPrompt,
  setShowPinPrompt,
  showMfaStep,
  showKeychainSetup,
  showPinLogin,
  onForgotPassword,
  onSubmit,
  onPinSubmit,
  onFallbackToPassword,
  onKeychainSetup,
  onCancelSetup,
  onMfaVerify,
  onMfaBack,
  onOpenQRScan,
}) => {
  const PinLoginPrompt = () => (
    <div className="pin-prompt-container">
      <button
        onClick={() => setShowPinPrompt(false)}
        className="pin-prompt-close"
        aria-label="Close"
      >
        &times;
      </button>
      <h4>{t.pinPromptTitle}</h4>
      <ol>
        <a
          href="https://chromewebstore.google.com/detail/happychef-keychain/kgmofdjbolplmhoflgpnkeacjjffbohi"
          target="_blank"
          rel="noopener noreferrer"
          style={{ textDecoration: 'underline', color: 'inherit' }}
        >
          <li>{t.pinPromptStep1}</li>
        </a>
        <li>{t.pinPromptStep2}</li>
        <li>{t.pinPromptStep3}</li>
        <li>{t.pinPromptStep4}</li>
      </ol>
    </div>
  );

  const renderLoginForm = () => {
    if (showMfaStep) {
      return (
        <MfaLogin
          onVerify={onMfaVerify}
          onBack={onMfaBack}
          error={error}
          loading={loading}
          username={username}
          translations={t}
        />
      );
    }

    if (showKeychainSetup) {
      return (
        <KeychainSetup
          onSetupSubmit={onKeychainSetup}
          onCancel={onCancelSetup}
          loading={loading}
          error={error}
          translations={t}
        />
      );
    }

    if (showPinLogin) {
      return (
        <PinLogin
          onPinSubmit={onPinSubmit}
          onFallback={onFallbackToPassword}
          error={error}
          loading={loading}
          translations={t}
        />
      );
    }

    return (
      <>
        <h2 className="login-welcome">{t.welcomeBack}</h2>
        <p className="login-subtitle">{t.loginSubtitle}</p>
        {showPinPrompt && <PinLoginPrompt />}

        {error && (
          <div className="error-message" role="alert">
            <FaExclamationCircle size={18} />
            <span>{error}</span>
          </div>
        )}
        {success && (
          <div className="success-message" role="status">
            <FaCheckCircle size={18} />
            <span>{success}</span>
          </div>
        )}

        <form className="login-form-expanded" onSubmit={onSubmit}>
          <div className="form-group">
            <label htmlFor="username">{t.username}</label>
            <div className="input-with-icon">
              <FaUser size={16} className="input-icon" />
              <input
                id="username"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="login-input-expanded"
                required
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="password">{t.password}</label>
            <div className="input-with-icon">
              <FaLock size={16} className="input-icon" />
              <input
                id="password"
                type="password"
                value={password}
                onChange={handlePasswordChange}
                className="login-input-expanded"
                required
              />
            </div>
          </div>

          <div className="form-options">
            <div className="remember-me">
              <input
                type="checkbox"
                id="remember"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
              />
              <label htmlFor="remember">{t.rememberMe}</label>
            </div>
            <a href="#" onClick={onForgotPassword} className="forgot-password">
              {t.forgotPassword}
            </a>
          </div>

          <button type="submit" className="login-button-expanded" disabled={loading}>
            {loading ? (
              <>
                <span className="spinner" />
                <span>{t.loggingIn}</span>
              </>
            ) : (
              <>
                <FaSignInAlt size={18} />
                <span>{t.login}</span>
              </>
            )}
          </button>

          <button
            type="button"
            className="qr-login-button"
            onClick={onOpenQRScan}
            disabled={loading}
          >
            <FaQrcode size={18} />
            <span>{t.qrLogin}</span>
          </button>
        </form>
      </>
    );
  };

  return (
    <div className="white-center-container">
      <div className="login-branding">
        <div className="brand-content">
          <div className="logo-container">
            <a
              href="https://happychef.be"
              target="_blank"
              rel="noopener noreferrer"
              className="brand-logo-link"
            >
              <div className="brand-logo">
                <img
                  src="https://static.reservaties.net/images/logo/HappyChef.png"
                  alt="HappyChef Logo"
                  className="brand-logo-image"
                />
              </div>
            </a>
          </div>
          <div className="brand-features">
            <div className="feature-item">
              <div className="feature-icon">
                <FaCalendarAlt size={20} />
              </div>
              <div className="feature-text">
                <h3>{t.feature1Title}</h3>
                <p>{t.feature1Desc}</p>
              </div>
            </div>
            <div className="feature-item">
              <div className="feature-icon">
                <FaGift size={20} />
              </div>
              <div className="feature-text">
                <h3>{t.feature2Title}</h3>
                <p>{t.feature2Desc}</p>
              </div>
            </div>
            <div className="feature-item">
              <div className="feature-icon">
                <FaHeart size={20} />
              </div>
              <div className="feature-text">
                <h3>{t.feature3Title}</h3>
                <p>
                  {t.feature3DescPrefix}
                  <a
                    href="https://happychef.cloud/onze-missie.html"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ textDecoration: 'underline', color: 'inherit' }}
                  >
                    {t.feature3DescLink}
                  </a>
                </p>
              </div>
            </div>
          </div>
          <div className="brand-footer">
            <p>{t.noAccount}</p>
            <a href={newAccountUrl} className="brand-cta" rel="noopener noreferrer">
              {t.registerNow}
            </a>
          </div>
        </div>
      </div>

      <div className="login-panel">
        <div className="login-form-container">
          {renderLoginForm()}

          <div className="login-footer">
            <p>{t.footer}</p>

            <div
              style={{
                marginTop: 6,
                fontSize: 14,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: 8,
                color: '#718096ce',
              }}
            >
              <a
                href="https://voorwaarden.happychef.be/#algemene-voorwaarden"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: 'inherit', textDecoration: 'none' }}
                aria-label={t.termsOfUse}
              >
                {t.termsOfUse}
              </a>
              <span aria-hidden="true" style={{ opacity: 0.6 }}>
                •
              </span>
              <a
                href="https://voorwaarden.happychef.be/#privacybeleid"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: 'inherit', textDecoration: 'none' }}
                aria-label={t.privacyPolicy}
              >
                {t.privacyPolicy}
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginContent;
